export interface User {
  id: string;
  name: string;
  email: string;
  avatar: string;
  role: string;
  department: string;
  permissions: string[];
}

export interface ChatSession {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  preview: string;
  userId: string;
  sharedWith?: SharedInfo[];
  tags?: string[];
  bookmarked?: boolean;
}

export interface SharedInfo {
  userId: string;
  userName: string;
  sharedAt: string;
  canEdit: boolean;
}

export interface MetricCard {
  label: string;
  value: string;
  change: number;
  icon: string;
}

export interface HourlyData {
  hour: string;
  consultas: number;
}

export interface DailyData {
  date: string;
  consultas: number;
  usuarios: number;
}

export interface QueryTypeData {
  type: string;
  count: number;
  percentage: number;
}

export interface ServiceData {
  service: string;
  count: number;
  infracciones: number;
}

export interface TopUser {
  name: string;
  email: string;
  queries: number;
  lastActive: string;
  department: string;
}

export interface ActivityItem {
  id: string;
  type: 'query' | 'share' | 'export' | 'login';
  user: string;
  description: string;
  timestamp: string;
}

export const CURRENT_USER: User = {
  id: 'u1',
  name: 'María García',
  email: 'ma.garcia@uniandes.edu.co',
  avatar: 'MG',
  role: 'Analista',
  department: 'Apoyo Financiero',
  permissions: [],
};

export const TEAM_USERS: User[] = [
  CURRENT_USER,
  { id: 'u2', name: 'Carlos Rodríguez', email: 'c.rodriguez@uniandes.edu.co', avatar: 'CR', role: 'Coordinador', department: 'Apoyo Financiero', permissions: [] },
  { id: 'u3', name: 'Ana Martínez', email: 'a.martinez@uniandes.edu.co', avatar: 'AM', role: 'Analista', department: 'Matrículas', permissions: [] },
  { id: 'u4', name: 'Juan López', email: 'j.lopez@uniandes.edu.co', avatar: 'JL', role: 'Director', department: 'Apoyo Financiero', permissions: [] },
  { id: 'u5', name: 'Sofía Hernández', email: 's.hernandez@uniandes.edu.co', avatar: 'SH', role: 'Analista', department: 'Apoyo Financiero', permissions: [] },
  { id: 'u6', name: 'Andrés Ramírez', email: 'a.ramirez@uniandes.edu.co', avatar: 'AR', role: 'Asistente', department: 'Registro', permissions: [] },
  { id: 'u7', name: 'Valentina Díaz', email: 'v.diaz@uniandes.edu.co', avatar: 'VD', role: 'Analista', department: 'Apoyo Financiero', permissions: [] },
  { id: 'u8', name: 'Diego Morales', email: 'd.morales@uniandes.edu.co', avatar: 'DM', role: 'Coordinador', department: 'Matrículas', permissions: [] },
];

export const METRIC_CARDS: MetricCard[] = [
  { label: 'Total Consultas', value: '2,847', change: 12.5, icon: 'search' },
  { label: 'Usuarios Activos', value: '23', change: 8.3, icon: 'users' },
  { label: 'Tiempo Promedio', value: '2.3s', change: -15.2, icon: 'clock' },
  { label: 'Cumplimiento ANS', value: '94.2%', change: 3.1, icon: 'shield' },
];

export const HOURLY_DATA: HourlyData[] = [
  { hour: '6am', consultas: 3 }, { hour: '7am', consultas: 8 },
  { hour: '8am', consultas: 25 }, { hour: '9am', consultas: 48 },
  { hour: '10am', consultas: 62 }, { hour: '11am', consultas: 55 },
  { hour: '12pm', consultas: 30 }, { hour: '1pm', consultas: 28 },
  { hour: '2pm', consultas: 52 }, { hour: '3pm', consultas: 58 },
  { hour: '4pm', consultas: 45 }, { hour: '5pm', consultas: 32 },
  { hour: '6pm', consultas: 12 }, { hour: '7pm', consultas: 5 },
];

export const DAILY_DATA: DailyData[] = Array.from({ length: 30 }, (_, i) => {
  const date = new Date(2026, 2, i + 1);
  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
  return {
    date: `${date.getDate()} Mar`,
    consultas: isWeekend ? Math.floor(Math.random() * 20) + 5 : Math.floor(Math.random() * 80) + 40,
    usuarios: isWeekend ? Math.floor(Math.random() * 5) + 1 : Math.floor(Math.random() * 15) + 8,
  };
});

export const QUERY_TYPES: QueryTypeData[] = [
  { type: 'Análisis de infracciones', count: 485, percentage: 17 },
  { type: 'Estado de solicitudes', count: 412, percentage: 14.5 },
  { type: 'Recurrencia de usuarios', count: 389, percentage: 13.7 },
  { type: 'Distribución por servicio', count: 342, percentage: 12 },
  { type: 'Tendencias temporales', count: 298, percentage: 10.5 },
  { type: 'Causales de incumplimiento', count: 265, percentage: 9.3 },
  { type: 'Tiempos de resolución', count: 231, percentage: 8.1 },
  { type: 'Comparativas periódicas', count: 198, percentage: 7 },
  { type: 'Otros', count: 227, percentage: 7.9 },
];

export const SERVICE_DATA: ServiceData[] = [
  { service: 'Becas y financiamiento', count: 1250, infracciones: 45 },
  { service: 'Matrícula financiera', count: 980, infracciones: 38 },
  { service: 'Pagos y facturación', count: 875, infracciones: 22 },
  { service: 'Devoluciones', count: 650, infracciones: 55 },
  { service: 'Créditos educativos', count: 520, infracciones: 30 },
  { service: 'Apoyos socioeconómicos', count: 445, infracciones: 18 },
  { service: 'Descuentos y exenciones', count: 380, infracciones: 12 },
  { service: 'Convenios financieros', count: 290, infracciones: 8 },
];

export const TOP_USERS: TopUser[] = [
  { name: 'María García', email: 'ma.garcia@uniandes.edu.co', queries: 342, lastActive: 'Hace 5 min', department: 'Apoyo Financiero' },
  { name: 'Carlos Rodríguez', email: 'c.rodriguez@uniandes.edu.co', queries: 289, lastActive: 'Hace 15 min', department: 'Apoyo Financiero' },
  { name: 'Sofía Hernández', email: 's.hernandez@uniandes.edu.co', queries: 245, lastActive: 'Hace 1 hora', department: 'Apoyo Financiero' },
  { name: 'Juan López', email: 'j.lopez@uniandes.edu.co', queries: 198, lastActive: 'Hace 2 horas', department: 'Apoyo Financiero' },
  { name: 'Valentina Díaz', email: 'v.diaz@uniandes.edu.co', queries: 176, lastActive: 'Hace 30 min', department: 'Apoyo Financiero' },
  { name: 'Andrés Ramírez', email: 'a.ramirez@uniandes.edu.co', queries: 154, lastActive: 'Hoy 8:30am', department: 'Registro' },
  { name: 'Ana Martínez', email: 'a.martinez@uniandes.edu.co', queries: 132, lastActive: 'Ayer', department: 'Matrículas' },
  { name: 'Diego Morales', email: 'd.morales@uniandes.edu.co', queries: 98, lastActive: 'Ayer', department: 'Matrículas' },
];

export const CHAT_SESSIONS: ChatSession[] = [
  {
    id: 'cs1', title: 'Análisis de infracciones ANS - Marzo 2026',
    createdAt: '2026-03-27T09:15:00', updatedAt: '2026-03-27T10:30:00',
    messageCount: 24, preview: 'Identifiqué 45 infracciones en el servicio de Becas y financiamiento...',
    userId: 'u1', tags: ['infracciones', 'ANS', 'becas'], bookmarked: true,
    sharedWith: [{ userId: 'u2', userName: 'Carlos Rodríguez', sharedAt: '2026-03-27T11:00:00', canEdit: false }],
  },
  {
    id: 'cs2', title: 'Recurrencia de solicitudes - Febrero',
    createdAt: '2026-03-25T14:20:00', updatedAt: '2026-03-25T15:45:00',
    messageCount: 18, preview: 'Los usuarios con mayor recurrencia en febrero fueron del servicio de Matrícula...',
    userId: 'u1', tags: ['recurrencia', 'matrícula'],
  },
  {
    id: 'cs3', title: 'Tendencia de Devoluciones Q1 2026',
    createdAt: '2026-03-22T11:00:00', updatedAt: '2026-03-22T12:30:00',
    messageCount: 31, preview: 'Las devoluciones presentaron un incremento del 23% respecto al trimestre anterior...',
    userId: 'u1', tags: ['devoluciones', 'tendencia'], bookmarked: true,
    sharedWith: [
      { userId: 'u4', userName: 'Juan López', sharedAt: '2026-03-22T13:00:00', canEdit: false },
      { userId: 'u5', userName: 'Sofía Hernández', sharedAt: '2026-03-22T13:05:00', canEdit: true },
    ],
  },
  {
    id: 'cs4', title: 'Causales de incumplimiento más frecuentes',
    createdAt: '2026-03-20T08:30:00', updatedAt: '2026-03-20T09:50:00',
    messageCount: 15, preview: 'La causal más frecuente es "Falta de información del estudiante" con un 28%...',
    userId: 'u1', tags: ['causales', 'incumplimiento'],
  },
  {
    id: 'cs5', title: 'Comparativa servicios Ene vs Feb 2026',
    createdAt: '2026-03-18T16:00:00', updatedAt: '2026-03-18T17:20:00',
    messageCount: 22, preview: 'El servicio de Créditos educativos tuvo un aumento significativo...',
    userId: 'u1', tags: ['comparativa', 'servicios'],
  },
];

export const SHARED_WITH_ME: ChatSession[] = [
  {
    id: 'cs10', title: 'Reporte mensual de SLA - Febrero 2026',
    createdAt: '2026-03-15T10:00:00', updatedAt: '2026-03-15T11:30:00',
    messageCount: 28, preview: 'El cumplimiento general de ANS fue del 91.3% en febrero...',
    userId: 'u2', tags: ['SLA', 'reporte', 'mensual'], bookmarked: false,
    sharedWith: [{ userId: 'u1', userName: 'María García', sharedAt: '2026-03-15T12:00:00', canEdit: false }],
  },
  {
    id: 'cs11', title: 'Análisis de carga operativa por área',
    createdAt: '2026-03-12T09:00:00', updatedAt: '2026-03-12T10:45:00',
    messageCount: 19, preview: 'El área de Matrículas concentra el 35% de las solicitudes totales...',
    userId: 'u4', tags: ['carga', 'operativa'],
    sharedWith: [{ userId: 'u1', userName: 'María García', sharedAt: '2026-03-12T11:00:00', canEdit: true }],
  },
  {
    id: 'cs12', title: 'Detección de patrones en solicitudes recurrentes',
    createdAt: '2026-03-10T14:00:00', updatedAt: '2026-03-10T15:30:00',
    messageCount: 35, preview: 'Se identificaron 3 patrones principales de recurrencia en solicitudes de becas...',
    userId: 'u5', tags: ['patrones', 'recurrencia', 'becas'],
    sharedWith: [{ userId: 'u1', userName: 'María García', sharedAt: '2026-03-10T16:00:00', canEdit: false }],
  },
];

export const RECENT_ACTIVITY: ActivityItem[] = [
  { id: 'a1', type: 'query', user: 'María García', description: 'Consultó infracciones ANS del mes', timestamp: '2026-03-27T10:30:00' },
  { id: 'a2', type: 'share', user: 'Carlos Rodríguez', description: 'Compartió "Reporte mensual SLA"', timestamp: '2026-03-27T10:15:00' },
  { id: 'a3', type: 'export', user: 'Sofía Hernández', description: 'Exportó análisis de devoluciones', timestamp: '2026-03-27T09:45:00' },
  { id: 'a4', type: 'query', user: 'Juan López', description: 'Consultó tendencia de solicitudes', timestamp: '2026-03-27T09:30:00' },
  { id: 'a5', type: 'login', user: 'Valentina Díaz', description: 'Inició sesión', timestamp: '2026-03-27T09:00:00' },
  { id: 'a6', type: 'query', user: 'Andrés Ramírez', description: 'Consultó recurrencia por servicio', timestamp: '2026-03-27T08:45:00' },
  { id: 'a7', type: 'share', user: 'Ana Martínez', description: 'Compartió "Análisis carga operativa"', timestamp: '2026-03-27T08:30:00' },
  { id: 'a8', type: 'export', user: 'María García', description: 'Descargó sesión de chat', timestamp: '2026-03-26T17:00:00' },
];

export const QUERY_TEMPLATES = [
  { id: 't1', title: 'Infracciones ANS del mes', prompt: '¿Cuántas infracciones de ANS se registraron este mes y cuáles fueron los servicios con mayor incumplimiento?', category: 'Infracciones' },
  { id: 't2', title: 'Top servicios con solicitudes', prompt: '¿Cuáles son los 5 servicios con más solicitudes registradas y cuál es su tasa de resolución?', category: 'Servicios' },
  { id: 't3', title: 'Usuarios recurrentes', prompt: 'Identifica los usuarios con mayor recurrencia de solicitudes este mes y los servicios que más consultan.', category: 'Recurrencia' },
  { id: 't4', title: 'Causales de incumplimiento', prompt: '¿Cuáles son las causales de incumplimiento más frecuentes y qué servicios concentran la mayoría?', category: 'Causales' },
  { id: 't5', title: 'Tendencia semanal', prompt: 'Muestra la tendencia de solicitudes de la última semana comparada con la semana anterior.', category: 'Tendencias' },
  { id: 't6', title: 'Tiempos de resolución', prompt: '¿Cuál es el tiempo promedio de resolución por tipo de servicio y cómo se compara con el ANS establecido?', category: 'Tiempos' },
  { id: 't7', title: 'Análisis de devoluciones', prompt: 'Analiza las solicitudes de devolución del último trimestre: cantidad, motivos principales y tiempo de gestión.', category: 'Servicios' },
  { id: 't8', title: 'Resumen ejecutivo', prompt: 'Genera un resumen ejecutivo del estado actual de las solicitudes: volumen, cumplimiento ANS, servicios críticos y recomendaciones.', category: 'Reportes' },
];
